import React from 'react';
import { ContentCardProps } from '../../types/components';
import { BlogPost, SuccessStory } from '../../types/content';

// Type guard to check if content is a BlogPost
const isBlogPost = (content: BlogPost | SuccessStory): content is BlogPost => {
  return 'author' in content;
};

// Type guard to check if content is a SuccessStory
const isSuccessStory = (content: BlogPost | SuccessStory): content is SuccessStory => {
  return 'clientName' in content;
};

const ContentCard: React.FC<ContentCardProps> = ({ content, variant }) => {
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getAuthorOrClient = () => {
    if (isBlogPost(content)) {
      return content.author.name;
    } else if (isSuccessStory(content)) {
      return content.clientName;
    }
    return '';
  };

  const handleCardClick = () => {
    // Check for external link first
    if (isBlogPost(content) && content.link) {
      window.open(content.link, '_blank', 'noopener,noreferrer');
      return;
    }

    // Navigate to the full article
    const contentType = isBlogPost(content) ? 'blog' : 'success-story';
    window.open(`/${contentType}/${content.id}`, '_blank', 'noopener,noreferrer');
  };

  if (variant === 'featured') {
    return (
      <div 
        onClick={handleCardClick}
        className="group cursor-pointer bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden touch-manipulation"
      >
        <div className="p-4 sm:p-6">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-200">
            {content.title}
          </h3>
          
          <p className="text-gray-600 mb-4 line-clamp-3 text-sm sm:text-base">
            {content.excerpt}
          </p>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm text-gray-500 mb-4 space-y-2 sm:space-y-0">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <span>{formatDate(content.publishDate)}</span>
              {getAuthorOrClient() && (
                <>
                  <span>•</span>
                  <span>{getAuthorOrClient()}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Standard variant
  return (
    <div 
      onClick={handleCardClick}
      className="group cursor-pointer bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden border border-gray-200 touch-manipulation"
    >
      <div className="flex">
        <div className="flex-1 p-3 sm:p-4">
          <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-200 line-clamp-2">
            {content.title}
          </h4>
          
          <p className="text-gray-600 text-xs sm:text-sm mb-3 line-clamp-2">
            {content.excerpt}
          </p>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs text-gray-500 mb-2 space-y-1 sm:space-y-0">
            <div className="flex items-center space-x-2">
              <span>{formatDate(content.publishDate)}</span>
              {getAuthorOrClient() && (
                <>
                  <span>•</span>
                  <span className="font-medium truncate">
                    {getAuthorOrClient()}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentCard;